import { useState, useEffect } from "react";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import { updateOpenHouse } from "../../utils/firebasefunctions";
import { composeRecapSellerEmail } from "../../utils/openHouseEmailComposer";
import { sendOpenHouseEmail } from "../../utils/emailSender";
import { sendListingAgentCopy } from "../../utils/openHouseSend";
import { serverTimestamp } from "firebase/firestore";

export default function RecapPreviewModal({ open, onClose, openHouse, onSent }) {
  const [{ user, emailTemplates }] = useStateValue();
  const [subject, setSubject] = useState("");
  const [agentFeedback, setAgentFeedback] = useState("");
  const [buyerFeedback, setBuyerFeedback] = useState("");
  const [otherComments, setOtherComments] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !openHouse) return;
    const fb = openHouse?.feedback || {};
    setAgentFeedback(fb.agentFeedback || "");
    setBuyerFeedback(fb.buyerFeedback || "");
    setOtherComments(fb.otherComments || "");
    const { subject: s } = composeRecapSellerEmail(openHouse, fb, user, emailTemplates?.["recap-seller"]);
    setSubject(s);
  }, [open, openHouse?.id, user?.id]);

  if (!open || !openHouse || !openHouse.feedback) return null;

  const feedback = openHouse.feedback;
  const oh = openHouse;

  const handleSend = async () => {
    if (!oh.sellerEmail) {
      toast.error("This open house has no seller email on file. Cannot send recap.");
      return;
    }
    setSending(true);
    try {
      const editedFeedback = {
        ...oh.feedback,
        agentFeedback,
        buyerFeedback,
        otherComments,
      };
      const { text, html } = composeRecapSellerEmail(oh, editedFeedback, user, emailTemplates?.["recap-seller"]);
      const r = await sendOpenHouseEmail({
        userId: user.id,
        to: oh.sellerEmail,
        toName: oh.sellerName,
        subject,
        text,
        html,
        type: "recap",
        relatedDealId: oh.dealId,
        relatedOpenHouseId: oh.id,
      });
      if (!r.ok) {
        toast.error(`Send failed: ${r.error}`);
        return;
      }
      sendListingAgentCopy({
        oh,
        sender: user,
        subject,
        text,
        html,
        type: "recap",
        excludeEmails: [oh.sellerEmail],
      }).catch((err) =>
        console.error("Listing-agent recap copy failed:", err),
      );
      await updateOpenHouse(
        oh.id,
        {
          recapSentAt: serverTimestamp(),
          recapSentBy: user.id,
          pendingRecap: false,
        },
        user.id
      );
      toast.success("Recap sent.");
      onSent && onSent(oh.id);
      onClose();
    } catch (err) {
      console.error("RecapPreviewModal error:", err);
      toast.error("Could not send recap.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[calc(100vh-32px)]">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Send Open House Recap</h3>
          <p className="text-xs text-slate-500 mt-1">
            To: <strong>{oh.sellerName}</strong> · {oh.sellerEmail}
            <br />
            Property: {oh.propertyAddress}
          </p>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {/* Read-only context */}
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, color: "#374151" }}>
            <div><strong>Property:</strong> {oh.propertyAddress}</div>
            <div><strong>Date:</strong> {feedback.date || oh.date}</div>
            <div><strong>Time:</strong> {feedback.time || `${oh.startTime}–${oh.endTime}`}</div>
            <div><strong>Hosted by:</strong> {feedback.agentName}</div>
            <div><strong>Groups attended:</strong> {feedback.groups}</div>
            <div><strong>Potential 2nd showings:</strong> {feedback.secondShowings}</div>
          </div>

          {/* Editable subject */}
          <label className="block text-xs font-semibold text-slate-700 mb-1">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm mb-3"
          />

          {/* Editable feedback fields */}
          <label className="block text-xs font-semibold text-slate-700 mb-1">What showing agents said</label>
          <textarea
            rows={3}
            value={agentFeedback}
            onChange={(e) => setAgentFeedback(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm mb-3"
          />

          <label className="block text-xs font-semibold text-slate-700 mb-1">What buyers said</label>
          <textarea
            rows={3}
            value={buyerFeedback}
            onChange={(e) => setBuyerFeedback(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm mb-3"
          />

          <label className="block text-xs font-semibold text-slate-700 mb-1">Other comments (optional)</label>
          <textarea
            rows={2}
            value={otherComments}
            onChange={(e) => setOtherComments(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm mb-3"
          />

          <p className="text-xs text-slate-400">
            Sending from your connected Gmail (or team account if not connected).
            Edit anything above before clicking Send.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
